import {
  CHAT_V8_NEAR_BOTTOM_VIEWPORT_FRACTION,
  transcriptScrollMachine,
  type T_ChatV8ClientStatus,
} from "@lattice/shared-frontend/chat-v8";
import { useActorRef } from "@xstate/react";
import {
  isClientTraceActive,
  logClientTrace,
} from "@lattice/frontend/lib/observability/client-trace";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type UIEvent,
} from "react";

type T_UseWebTranscriptScrollArgs = {
  status: T_ChatV8ClientStatus;
};

type T_UseWebTranscriptScrollResult = {
  contentRef: RefObject<HTMLDivElement | null>;
  /** True while the reader is far enough up that a jump control is useful. */
  isAwayFromBottom: boolean;
  /** True while content exists above the viewport (top edge fade). */
  hasContentAbove: boolean;
  jumpToBottom: () => void;
  notifyScroll: (event: UIEvent<HTMLElement>) => void;
  notifyUserScrollIntent: () => void;
  scrollOwnerRef: RefObject<HTMLElement | null>;
};

/** Distance from the bottom, in px, past which the jump control appears. */
const BOTTOM_JUMP_THRESHOLD = 240;

/** Distance from an edge, in px, still treated as "at the edge". */
const EDGE_FADE_EPSILON = 4;

function getActiveAnimationCount(content: HTMLElement): number {
  try {
    return content.getAnimations({ subtree: true }).length;
  } catch {
    // Older mobile browsers may not support the subtree option. Diagnostics
    // must never interfere with transcript scrolling.
    return -1;
  }
}

/** Web DOM adapter for the shared transcript scroll state machine. */
export function useWebTranscriptScroll({
  status,
}: T_UseWebTranscriptScrollArgs): T_UseWebTranscriptScrollResult {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollOwnerRef = useRef<HTMLElement>(null);
  // Presentation-only fact about scroll offset; the machine owns *where* the
  // transcript should be, not whether an affordance is visible.
  const [isAwayFromBottom, setIsAwayFromBottom] = useState(false);
  const [hasContentAbove, setHasContentAbove] = useState(false);
  const actor = useActorRef(transcriptScrollMachine, {
    input: {
      scrollToOffset: (offset) => {
        const scrollOwner = scrollOwnerRef.current;
        if (!scrollOwner) return;
        scrollOwner.scrollTop = offset;
        if (isClientTraceActive()) {
          logClientTrace({
            event: "scroll.programmatic",
            scope: "chat-v8.scroll",
            attributes: {
              client_height: scrollOwner.clientHeight,
              requested_offset: offset,
              scroll_height: scrollOwner.scrollHeight,
              scroll_top: scrollOwner.scrollTop,
            },
          });
        }
      },
    },
  });
  const shouldPin = status === "sending" || status === "streaming";

  // eslint-disable-next-line no-restricted-syntax -- ResizeObserver is the external DOM measurement boundary for the shared scroll machine.
  useEffect(() => {
    const scrollOwner = scrollOwnerRef.current;
    const content = contentRef.current;
    if (!scrollOwner || !content) return;

    const notifyMeasurements = () => {
      actor.send({ type: "LAYOUT", viewportHeight: scrollOwner.clientHeight });
      actor.send({
        type: "CONTENT_SIZE_CHANGED",
        contentHeight: scrollOwner.scrollHeight,
        pin: shouldPin,
      });
      // The top fade must be correct on first paint and after content grows,
      // not only once the reader scrolls.
      setHasContentAbove(scrollOwner.scrollTop > EDGE_FADE_EPSILON);
      if (isClientTraceActive()) {
        logClientTrace({
          event: "content.measured",
          scope: "chat-v8.scroll",
          attributes: {
            active_animation_count: getActiveAnimationCount(content),
            chat_status: status,
            client_height: scrollOwner.clientHeight,
            content_length: content.textContent?.length ?? 0,
            distance_from_bottom:
              scrollOwner.scrollHeight -
              (scrollOwner.scrollTop + scrollOwner.clientHeight),
            machine_state: actor.getSnapshot().value,
            should_pin: shouldPin,
            scroll_height: scrollOwner.scrollHeight,
            scroll_top: scrollOwner.scrollTop,
          },
        });
      }
    };

    notifyMeasurements();
    const observer = new ResizeObserver(notifyMeasurements);
    observer.observe(scrollOwner);
    observer.observe(content);
    return () => observer.disconnect();
  }, [actor, shouldPin, status]);

  const notifyScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const scrollOwner = event.currentTarget;
      const distanceFromBottom =
        scrollOwner.scrollHeight -
        (scrollOwner.scrollTop + scrollOwner.clientHeight);
      const isNearBottom =
        distanceFromBottom <
        scrollOwner.clientHeight * CHAT_V8_NEAR_BOTTOM_VIEWPORT_FRACTION;
      setIsAwayFromBottom(distanceFromBottom > BOTTOM_JUMP_THRESHOLD);
      setHasContentAbove(scrollOwner.scrollTop > EDGE_FADE_EPSILON);
      actor.send({
        type: "SCROLLED",
        isNearBottom,
      });
      if (isClientTraceActive()) {
        logClientTrace({
          event: "scroll.position.changed",
          scope: "chat-v8.scroll",
          attributes: {
            client_height: scrollOwner.clientHeight,
            distance_from_bottom: distanceFromBottom,
            is_near_bottom: isNearBottom,
            machine_state: actor.getSnapshot().value,
            scroll_height: scrollOwner.scrollHeight,
            scroll_top: scrollOwner.scrollTop,
          },
        });
      }
    },
    [actor],
  );
  const jumpToBottom = useCallback(() => {
    const scrollOwner = scrollOwnerRef.current;
    setIsAwayFromBottom(false);

    // Re-measure before jumping. The machine's cached content/viewport heights
    // can lag live layout when a widget, chart, or image finishes loading, and
    // a stale height lands the reader short of the bottom.
    if (scrollOwner) {
      actor.send({ type: "LAYOUT", viewportHeight: scrollOwner.clientHeight });
      actor.send({
        type: "CONTENT_SIZE_CHANGED",
        contentHeight: scrollOwner.scrollHeight,
        pin: false,
      });
    }
    actor.send({ type: "JUMP_TO_BOTTOM" });
    if (isClientTraceActive()) {
      logClientTrace({
        event: "scroll.jump_to_bottom",
        scope: "chat-v8.scroll",
        attributes: { machine_state: actor.getSnapshot().value },
      });
    }
  }, [actor]);

  const notifyUserScrollIntent = useCallback(() => {
    actor.send({ type: "USER_DRAG_STARTED" });
    if (isClientTraceActive()) {
      logClientTrace({
        event: "scroll.user_intent",
        scope: "chat-v8.scroll",
        attributes: { machine_state: actor.getSnapshot().value },
      });
    }
  }, [actor]);

  return {
    contentRef,
    hasContentAbove,
    isAwayFromBottom,
    jumpToBottom,
    notifyScroll,
    notifyUserScrollIntent,
    scrollOwnerRef,
  };
}
